import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import NavBar from '../../../audi-ui-components/NavBar';
import AlertRed from '../../../audi-ui-components/icons/AlertRed';
import AlertOrange from '../../../audi-ui-components/icons/AlertOrange';
import TickGreen from '../../../audi-ui-components/icons/TickGreen';
import { gtmPush } from '../../../lib/gtm';

const QuickCheckRecalls = ({ recalls, isAuthed, recallError }) => {

    const [activeTab, setActiveTab] = useState(0);


    // **************

    const USE_TEST_DATA = false; // set to false to disable extra test recalls

    // temporary create some test data
    let testRecalls = recalls || [];

    // Find completed recalls and duplicate them
    const completedRecalls = testRecalls.filter(recall =>
        recall.status?.toLowerCase() === 'completed' ||
        recall.status?.toLowerCase() === 'closed' ||
        recall.status?.toLowerCase() === 'repaired'
    );

    if (USE_TEST_DATA && completedRecalls.length > 0) {
        const firstCompleted = completedRecalls[0];

        // Create 2 additional completed recalls with modified names
        const additionalCompleted = [
            {
                ...firstCompleted,
                name: `${firstCompleted.name} - Additional Check 1 TEST`,
                campaignCode: `${firstCompleted.campaignCode}-A1`
            },
            {
                ...firstCompleted,
                name: `${firstCompleted.name} - Additional Check 2 TEST`,
                campaignCode: `${firstCompleted.campaignCode}-A2`
            }
        ];

        // Create 2 outstanding recalls with different names
        const outstandingRecalls = [
            {
                ...firstCompleted,
                name: `Brake System Update TEST - ${firstCompleted.name}`,
                status: 'Outstanding',
                campaignCode: `${firstCompleted.campaignCode}-OUT1`,
                campaignType: 'Service Campaign',
                description: 'Brake system software update required for optimal performance',
                repairDate: null
            },
            {
                ...firstCompleted,
                name: `Engine Management Update TEST - ${firstCompleted.name}`,
                status: 'Outstanding',
                campaignCode: `${firstCompleted.campaignCode}-OUT2`,
                campaignType: 'Service Campaign',
                description: 'Engine management system requires firmware update',
                repairDate: null
            },
            {
                ...firstCompleted,
                name: `Airbag Safety System Check TEST`,
                status: 'Outstanding',
                campaignCode: `${firstCompleted.campaignCode}-SAFETY1`,
                campaignType: 'Safety Recall',
                description: 'Critical safety recall - airbag system requires immediate inspection and potential replacement',
                repairDate: null
            },
            {
                ...firstCompleted,
                name: `Fuel System Safety Recall TEST`,
                status: 'Outstanding',
                campaignCode: `${firstCompleted.campaignCode}-SAFETY2`,
                campaignType: 'Safety Recall',
                description: 'Safety recall - fuel system component requires inspection and replacement',
                repairDate: null
            }
        ];

        // Add all test recalls to the array
        testRecalls = [...testRecalls, ...additionalCompleted, ...outstandingRecalls];
    }

    // Use testRecalls instead of recalls for the rest of the component
    recalls = testRecalls;

    // ***********

    if (!Array.isArray(recalls) || recalls.length === 0) {
        if (recallError) {
            return <p className="recalls-none" style={{ color: 'red' }}>Quick Check is unable to retrieve Recall or Service Updates data for this vehicle. Please contact your local Audi Dealer for more information.</p>;
        }
        return <p className="recalls-none">No safety and performance notices found for your vehicle.</p>;
    }

    const getIcon = (recall) => {
        const status = recall.status?.toLowerCase();
        if (status === 'completed' || status === 'closed' || status === 'repaired') {
            return <TickGreen small />;
        }
        if (recall.campaignType === 'Service Campaign' || recall.campaignType === 'Local Campaign') {
            return <AlertOrange small />;
        }
        return <AlertRed small />;
    };

    const getStatusGroup = (status) => {
        const s = status?.toLowerCase();
        if (s === 'completed' || s === 'closed' || s === 'repaired') return 'Completed';
        if (s === 'outstanding' || s === 'initiated') return 'Outstanding';
        return null;
    };

    const recallsByStatus = recalls.reduce((groups, recall) => {
        const group = getStatusGroup(recall.status);
        if (!group) return groups;
        if (!groups[group]) groups[group] = [];
        groups[group].push(recall);
        return groups;
    }, {});

    const hasOutstanding = recallsByStatus['Outstanding']?.length > 0;
    const hasCompleted = recallsByStatus['Completed']?.length > 0;

    if (!hasOutstanding && !hasCompleted) {
        return null;
    }

    const tabActions = [];
    if (hasOutstanding) tabActions.push({ label: "Outstanding" });
    if (hasCompleted) tabActions.push({ label: "Completed" });

    const outstandingTabIndex = hasOutstanding ? 0 : -1;
    const completedTabIndex = hasOutstanding && hasCompleted ? 1 : (hasCompleted ? 0 : -1);

    const renderRecallItem = (recall, idx) => (
        <div key={`recall-${idx}`} className="recall-item">
            <div className="recall-header">
                <div className="recall-icon">
                    {getIcon(recall)}
                </div>
                <div className="recall-headline">
                    <span className="recall-field"><b>Recall Alert:</b> {recall.name}</span>
                    <span className="recall-sep"> | </span>
                    <span className="recall-field"><b>Campaign code:</b> {recall.campaignCode}</span>
                    <span className="recall-sep"> | </span>
                    <span className="recall-field"><b>Status:</b> {recall.status}</span>
                </div>
            </div>
        </div>
    );

    const renderRecallItemNoIcon = (recall, idx, showBookLink = false) => (
        <div key={`recall-${idx}`} className="recall-item">
            <div className="recall-headline">
                <span className="recall-field"><b>Recall Alert:</b> {recall.name}</span>
                <span className="recall-sep"> | </span>
                <span className="recall-field"><b>Campaign code:</b> {recall.campaignCode}</span>
                <span className="recall-sep"> | </span>
                <span className="recall-field"><b>Status:</b> {recall.status}</span>
            </div>
            {showBookLink && (
                <a
                    href="https://forms.audi.com.au/book-a-service?ui=audi"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="recall-book-link"
                    onClick={() => gtmPush("buyServicePlan", "BspBookAService", "BspChoose")}
                >
                    Book your service &gt;
                </a>
            )}
        </div>
    );

    const renderCampaignSummary = () => (
        <div className="recall-item recall-item--campaign">
            <div className="recall-header">
                <div className="recall-icon">
                    <AlertOrange small />
                </div>
                <div className="recall-headline">
                    <span className="recall-field"><b>Service Campaign Notice</b></span>
                    <span className="recall-sep"> | </span>
                    <span className="recall-field"><b>Status:</b> Outstanding</span>
                </div>
            </div>
            <div className="recall-description">This vehicle has an open service campaign(s) that requires attention.</div>
            <Link to={isAuthed ? "/" : "/login"} className="recall-book-link">Login to Audi Owners Portal &gt;</Link>
        </div>
    );

    const renderOutstanding = (items) => {
        const safetyRecalls = items.filter(r => r.campaignType !== 'Service Campaign');
        const campaignNotices = items.filter(r => r.campaignType === 'Service Campaign');
        return (
            <>
                {safetyRecalls.length > 0 && (
                    <>
                        <div className="recall-group-heading">
                            <div className="recall-icon"><AlertRed small /></div>
                            <b>Recalls</b>
                        </div>
                        {safetyRecalls.map((recall, idx) => renderRecallItemNoIcon(recall, idx, true))}
                    </>
                )}
                {campaignNotices.length > 0 && renderCampaignSummary()}
            </>
        );
    };

    return (
        <div className="quick-check-recalls">
            <p className="recalls-title"><b>Important Recall and Service Updates</b></p>
            <NavBar
                actions={tabActions}
                activeIndex={activeTab}
                onActionClick={(idx) => setActiveTab(idx)}
                navType="tab"
                className="recalls-nav mb-4"
            />
            {activeTab === outstandingTabIndex && hasOutstanding && (
                <div className="recalls-scroll">
                    {renderOutstanding(recallsByStatus['Outstanding'])}
                </div>
            )}
            {activeTab === completedTabIndex && hasCompleted && (
                <div className="recalls-scroll completed-recalls">
                    {recallsByStatus['Completed'].map(renderRecallItem)}
                </div>
            )}
        </div>
    );
};

export default QuickCheckRecalls;
